import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { RefreshCw, ExternalLink, Mail, HeartHandshake, Building2 } from "lucide-react";

const STATUS = {
  active: ["Active", "var(--olive)"],
  trialing: ["Trial", "var(--olive)"],
  past_due: ["Past due", "var(--terracotta)"],
  unpaid: ["Unpaid", "var(--terracotta)"],
  canceling: ["Canceling", "var(--gold)"],
  canceled: ["Canceled", "var(--muted)"],
  incomplete: ["Incomplete", "var(--gold)"],
  incomplete_expired: ["Expired", "var(--muted)"],
  none: ["No plan", "var(--muted)"],
  comped: ["Comped", "var(--ink-2)"],
  free_trial: ["Free trial", "var(--orange)"],
  awaiting_payment: ["Awaiting payment", "var(--gold)"],
  error: ["Couldn't check", "var(--terracotta)"],
};
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const money = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const DATE_LABEL = { past_due: "Retrying until", free_trial: "Trial ends", canceling: "Ends" };

function StatusPill({ status, testid }) {
  const [label, color] = STATUS[status] || [status, "var(--muted)"];
  return <span className="inline-flex text-[0.6rem] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full whitespace-nowrap" style={{ border: `1px solid ${color}`, color }} data-testid={testid}>{label}</span>;
}

function HealthRow({ r, onEmail, busy }) {
  const Icon = r.kind === "church" ? Building2 : HeartHandshake;
  const needsCard = r.status === "past_due" || r.status === "unpaid";
  const tid = `billing-row-${r.kind}-${r.id}`;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 py-3 border-b last:border-0" style={{ borderColor: "var(--border-subtle)" }} data-testid={tid}>
      <Icon size={16} className="shrink-0" style={{ color: "var(--muted)" }} />
      <div className="flex-1 min-w-[12rem]">
        <div className="text-sm font-medium truncate" style={{ color: "var(--ink)" }}>{r.name || "—"}</div>
        <div className="text-xs truncate" style={{ color: "var(--muted)" }}>{r.email || (r.kind === "church" ? "Church plan" : "Supporter")}</div>
        {r.error && <div className="text-[0.65rem] mt-0.5" style={{ color: "var(--terracotta)" }}>{r.error}</div>}
      </div>
      <StatusPill status={r.status} testid={`${tid}-status`} />
      <div className="w-24 text-xs text-right" style={{ color: "var(--ink-2)" }} data-testid={`${tid}-amount`}>
        <div className="text-[0.55rem] uppercase tracking-[0.16em]" style={{ color: "var(--muted)" }}>Monthly</div>
        {r.monthly_cents ? money(r.monthly_cents) : "—"}
      </div>
      <div className="w-32 text-xs" style={{ color: "var(--ink-2)" }} data-testid={`${tid}-next`}>
        <div className="text-[0.55rem] uppercase tracking-[0.16em]" style={{ color: "var(--muted)" }}>{DATE_LABEL[r.status] || "Next charge"}</div>
        {fmtDate(r.next_charge)}
      </div>
      <div className="flex items-center gap-2 shrink-0 w-[13rem] justify-end">
        {needsCard && (
          <button onClick={() => onEmail(r)} disabled={busy} className="h-8 px-3 rounded-full inline-flex items-center gap-1.5 text-xs transition-colors hover:bg-[var(--surface-hover)]" style={{ border: "1px solid var(--terracotta)", color: "var(--terracotta)" }} title={r.card_email_sent_at ? `Last sent ${fmtDate(r.card_email_sent_at)}` : "Email a link to update their card"} data-testid={`${tid}-card-email`}>
            <Mail size={13} /> {busy ? "Sending…" : r.card_email_sent_at ? "Resend card link" : "Send card link"}
          </button>
        )}
        {r.stripe_url && (
          <a href={r.stripe_url} target="_blank" rel="noreferrer" className="h-8 px-3 rounded-full inline-flex items-center gap-1.5 text-xs transition-colors hover:bg-[var(--surface-hover)]" style={{ border: "1px solid var(--border-strong)", color: "var(--ink-2)" }} data-testid={`${tid}-stripe`}>
            <ExternalLink size={13} /> Stripe
          </a>
        )}
      </div>
    </div>
  );
}

function IncomeCard({ income }) {
  const i = income || {};
  return (
    <div className="rounded-md p-5 mb-6 flex flex-wrap items-end gap-x-10 gap-y-4" style={{ background: "var(--bg-deep)", border: "1px solid var(--gold)" }} data-testid="billing-income-card">
      <div>
        <div className="text-[0.6rem] uppercase tracking-[0.2em]" style={{ color: "var(--gold)" }}>Monthly income</div>
        <div className="font-serif-display text-5xl leading-none mt-1" style={{ color: "var(--ink)" }} data-testid="billing-income-total">{income ? money(i.total) : "–"}</div>
        <div className="text-[0.65rem] mt-1" style={{ color: "var(--muted)" }}>From active subscriptions, per month</div>
      </div>
      {[["Supporters", i.supporters, "billing-income-supporters", "var(--ink-2)"], ["Church plans", i.churches, "billing-income-churches", "var(--ink-2)"], ["At risk (past due)", i.at_risk, "billing-income-at-risk", "var(--terracotta)"]].map(([label, v, tid, color]) => (
        <div key={tid}>
          <div className="text-[0.55rem] uppercase tracking-[0.16em]" style={{ color: "var(--muted)" }}>{label}</div>
          <div className="font-serif-display text-2xl" style={{ color }} data-testid={tid}>{income ? money(v) : "–"}</div>
        </div>
      ))}
    </div>
  );
}

function Counts({ rows, keys, prefix }) {
  return (
    <div className="flex flex-wrap gap-2 mb-2">
      {keys.map(([label, statuses]) => {
        const n = rows.filter((r) => statuses.includes(r.status)).length;
        return (
          <span key={label} className="text-xs px-3 py-1 rounded-full" style={{ background: "var(--surface-hover)", color: "var(--ink-2)" }} data-testid={`${prefix}-count-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            {label} <strong style={{ color: "var(--ink)" }}>{n}</strong>
          </span>
        );
      })}
    </div>
  );
}

function Group({ title, rows, keys, prefix, empty, onEmail, busyId }) {
  return (
    <div className="mb-6" data-testid={`${prefix}-group`}>
      <h4 className="overline mb-2" style={{ fontSize: "0.6rem", color: "var(--muted)" }}>{title} ({rows.length})</h4>
      <Counts rows={rows} keys={keys} prefix={prefix} />
      {rows.length === 0 ? <p className="text-sm py-3" style={{ color: "var(--muted)" }}>{empty}</p> : (
        <div>{rows.map((r) => <HealthRow key={`${r.kind}-${r.id}`} r={r} onEmail={onEmail} busy={busyId === r.id} />)}</div>
      )}
    </div>
  );
}

export default function BillingHealthPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { setData((await api.get("/admin/billing-health")).data); }
    catch { toast.error("Couldn't check billing with Stripe."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sendEmail = async (r) => {
    if (!window.confirm(`Email ${r.name} a link to update their card?`)) return;
    setBusyId(r.id);
    try {
      const res = await api.post(`/admin/billing-health/${r.kind}/${r.id}/card-email`);
      toast.success(`Card link sent to ${res.data.sent_to.join(", ")}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send the email."); }
    finally { setBusyId(""); }
  };

  const rows = data?.rows || [];
  return (
    <section className="card-surface p-6" data-testid="billing-health-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <h3 className="font-serif-display text-xl" style={{ color: "var(--ink)" }}>Billing health</h3>
          <p className="text-xs" style={{ color: "var(--muted)" }}>Live from Stripe: Supporters and churches.{data?.checked_at ? ` Checked ${new Date(data.checked_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}.` : ""}</p>
        </div>
        <button onClick={load} disabled={loading} className="btn-ghost text-xs" data-testid="billing-health-refresh"><RefreshCw size={13} className={loading ? "animate-spin" : ""} /> {loading ? "Checking Stripe…" : "Refresh"}</button>
      </div>
      <IncomeCard income={data?.income_cents} />
      {!data && loading && <p className="text-sm py-6" style={{ color: "var(--muted)" }}>Checking Stripe…</p>}
      {data && (
        <>
          <Group title="Supporters" prefix="billing-supporters" rows={rows.filter((r) => r.kind === "supporter")} empty="No Supporters yet." onEmail={sendEmail} busyId={busyId}
            keys={[["Active", ["active", "trialing"]], ["Past due", ["past_due", "unpaid"]], ["Canceling", ["canceling"]], ["Canceled", ["canceled", "none", "incomplete_expired"]]]} />
          <Group title="Churches" prefix="billing-churches" rows={rows.filter((r) => r.kind === "church")} empty="No churches yet." onEmail={sendEmail} busyId={busyId}
            keys={[["Paying", ["active", "trialing", "canceling"]], ["Free trial", ["free_trial"]], ["Comped", ["comped"]], ["Awaiting payment", ["awaiting_payment", "incomplete"]], ["Past due", ["past_due", "unpaid"]], ["Canceled", ["canceled", "none", "incomplete_expired"]]]} />
        </>
      )}
    </section>
  );
}
